import { StyleSheet, View, Dimensions } from 'react-native';
import { TextInput, Button, Surface, useTheme, Text } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { getDriverVehicles, getOnboardingState, getUserDetails } from '../../src/api/apiService';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LoadingScreen } from '../../src/components/LoadingScreen';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { getItem } from '../../src/lib/storage';
import { track, resolveEnvironment, makeSemanticEventId } from '../../src/lib/analytics';
import { AnalyticsEvent } from '../../src/lib/analyticsEvents';
import { isAuthoritativeOnboardingState } from '../../src/utils/onboardingResume';

const { width: screenWidth } = Dimensions.get('window');
const MAX_ICON_SIZE = 200; // Maximum size for the icon
const ICON_SIZE = Math.min(screenWidth * 0.4, MAX_ICON_SIZE);

type ContinueTarget =
  | '/register/driver'
  | '/onboarding/vehicle'
  | '/onboarding/permissions';

/**
 * Pure routing helper (MR !388 blocker A). Exported for unit tests.
 *
 * companyResolved + hasVehicles are independent signals:
 *   unresolved              -> canonical registration (company + explicit payer)
 *   resolved && !hasVehicle -> vehicle
 *   resolved && hasVehicle  -> permissions
 *
 * Support 5781: an unresolved company must go to the canonical registration
 * journey (`/register/driver`) which runs company -> EXPLICIT payer ->
 * association. The legacy `/onboarding/employment` screen associated without a
 * payer (D-031/D-053) and blindly pushed vehicle, so it must never be the
 * no-employment destination.
 */
export function resolveOnboardingContinueTarget(
  companyResolved: boolean,
  hasVehicles: boolean,
): ContinueTarget {
  if (!companyResolved) return '/register/driver';
  if (!hasVehicles) return '/onboarding/vehicle';
  return '/onboarding/permissions';
}

/**
 * Classify the driver onboarding flow from the RESOLVED checkUserData state.
 *
 * Pure + exported for unit tests. A driver who has already declared an employer
 * (an employment row — active OR the informative self-pay one) is RESUMING;
 * one with no employment is a NEW onboarding. This must be derived AFTER
 * checkUserData completes, never from the initial `false`/loading state (#389
 * blocker 3).
 */
export type DriverOnboardingState = {
  ok: boolean;
  isOwner: boolean;
  employments: Array<{ is_active?: boolean; status?: string }>;
};

export function classifyDriverOnboardingFlow(state: DriverOnboardingState): 'new' | 'resume' {
  if (!state.ok) return 'new';
  if (state.isOwner) return 'new';
  const hasEmployment =
    Array.isArray(state.employments) && state.employments.length > 0;
  return hasEmployment ? 'resume' : 'new';
}

/**
 * Module-level dedupe guard for `onboarding_started`.
 *
 * Keyed by the DETERMINISTIC semantic event id (see `makeSemanticEventId`), so a
 * remount / React StrictMode double-mount of the SAME logical journey collapses
 * to a single emission — independent of any component-local `useRef` guard,
 * which resets on unmount/remount and would otherwise let a second mount emit a
 * fresh random id that the analytics dedupe set cannot recognize.
 *
 * A genuinely new journey (distinct authenticated user, or a fresh session key)
 * yields a distinct id and therefore a distinct, allowed emission. The exported
 * reset keeps component unit tests isolated.
 */
const onboardingStartedEmitted = new Set<string>();
export const __resetOnboardingStartedGuardForTests = () => onboardingStartedEmitted.clear();

// Session-scoped persistent key: stable for the lifetime of the module (one app
// session), distinct from other sessions. Used only when no authenticated user
// id is available (ok=false / thrown error) so the semantic id is still stable
// across remounts — a genuinely new session produces a new key.
let onboardingSessionKey: string | null = null;
const getOnboardingSessionKey = (): string => {
  if (!onboardingSessionKey) {
    onboardingSessionKey = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return onboardingSessionKey;
};

const emitOnboardingStartedOnce = (flow: 'new' | 'resume', userId?: string | number | null) => {
  const eventId = makeSemanticEventId(AnalyticsEvent.OnboardingStarted, {
    userId: userId == null ? undefined : String(userId),
    flow,
    stableKey: userId == null ? getOnboardingSessionKey() : undefined,
  });
  if (onboardingStartedEmitted.has(eventId)) return;
  onboardingStartedEmitted.add(eventId);
  track(
    AnalyticsEvent.OnboardingStarted,
    { user_role: 'driver', flow, app_variant: resolveEnvironment() },
    { eventId },
  );
};

export default function OnboardingWelcome() {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const router = useRouter();
  const [isChecking, setIsChecking] = useState(true);
  // Persist both signals independently (blocker A).
  const [companyResolved, setCompanyResolved] = useState(false);
  const [hasVehicles, setHasVehicles] = useState(false);
  // Flow classification resolved only after checkUserData completes.
  const [flow, setFlow] = useState<'new' | 'resume'>('new');
  // Stable guard: onboarding_started fires EXACTLY ONCE, after checkUserData
  // resolves — never on the initial loading `new` state. A module-level dedupe
  // (see emitOnboardingStartedOnce) guarantees a remount / StrictMode
  // double-mount collapses to a single emission regardless of component
  // instance identity.
  const onboardingStartedRef = useRef(false);

  // Check if user already has employment and vehicle data
  useEffect(() => {
    const checkUserData = async () => {
      try {
        // First, verify the token is valid
        const token = await getItem('userToken');

        if (!token) {
          router.replace('/');
          return;
        }

        // Try to validate the token by calling getUserDetails
        const userDetailsResponse = await getUserDetails();

        if (!userDetailsResponse.ok) {
          // Don't redirect to login yet - show error to user
          // Token exists but backend doesn't recognize it. Classify consistently
          // with the thrown-error path: a new onboarding (no resolved resume fact
          // exists on an ok=false result) and emit exactly once with the SAME
          // deterministic eventId as the error path for this session.
          setCompanyResolved(false);
          setHasVehicles(false);
          setIsChecking(false);
          if (!onboardingStartedRef.current) {
            onboardingStartedRef.current = true;
            emitOnboardingStartedOnce('new');
          }
          return;
        }

        // If user is an owner OR has any owned companies, redirect them to dashboard
        // This page is only for drivers without companies
        const isOwner = userDetailsResponse.data?.groups?.includes('Owner');
        const hasOwnedCompanies =
          userDetailsResponse.data?.owned_companies &&
          userDetailsResponse.data.owned_companies.length > 0;

        if (isOwner || hasOwnedCompanies) {
          router.replace('/dashboard/home');
          return;
        }

        // #428: an authenticated driver with valid personal coverage and an
        // incomplete profile must return to the profile-recovery funnel. The
        // generic onboarding/permissions path would otherwise finish local
        // device setup and its authoritative dashboard guard would send the
        // same account back to /onboarding indefinitely.
        const profileRecovery = userDetailsResponse.data?.profile_recovery;
        if (
          profileRecovery?.eligible === true
          && Array.isArray(profileRecovery.missing_fields)
          && profileRecovery.missing_fields.length > 0
        ) {
          router.replace('/register/driver');
          return;
        }

        // Legacy-route recovery (#371): before this fix, the result CTA sent an
        // unknown-company lead through /dashboard/home and then into this generic
        // index. A lead intentionally has no Employment, so the old heuristic
        // classified it as a brand-new onboarding and asked for the company again.
        // Consult the authoritative pointer only for the otherwise-unresolved
        // no-employment branch and migrate it to the durable wait screen.
        const serializedEmployments = userDetailsResponse.data?.employments;
        if (!Array.isArray(serializedEmployments) || serializedEmployments.length === 0) {
          const onboardingStateResponse = await getOnboardingState().catch(() => null);
          if (
            !onboardingStateResponse
            || !onboardingStateResponse.ok
            || !isAuthoritativeOnboardingState(onboardingStateResponse.data)
          ) {
            router.replace('/onboarding/state-error');
            return;
          }
          if (
            onboardingStateResponse.data.persona === 'DRV-CO'
            && onboardingStateResponse.data.current_step === 'company_contact_submitted'
            && onboardingStateResponse.data.completed === false
          ) {
            router.replace('/onboarding/company-contact-pending');
            return;
          }
        }

        const [vehiclesResponse] = await Promise.all([getDriverVehicles()]);

        // MR !388 blocker A — separate `companyResolved` from `hasVehicles`.
        //   * companyResolved = the driver has ALREADY declared an employer
        //     (an employment row, whether active or the informative self-pay
        //     one). The company step must NEVER be re-asked.
        //   * hasVehicles = the driver has at least one vehicle configured.
        // Routing (exact):
        //   * !companyResolved               -> /register/driver (canonical
        //                                       company + explicit payer)
        //   * companyResolved && !hasVehicle -> /onboarding/vehicle
        //   * companyResolved && hasVehicle  -> /onboarding/permissions
        const resolved =
          userDetailsResponse.ok &&
          Array.isArray(userDetailsResponse.data?.employments) &&
          userDetailsResponse.data.employments.length > 0;
        const vehicles =
          Boolean(vehiclesResponse.ok) &&
          Boolean(vehiclesResponse.data?.has_vehicles);

        setCompanyResolved(resolved);
        setHasVehicles(vehicles);
        // Classify new vs resume from the RESOLVED state. onboarding_started is
        // emitted EXACTLY ONCE with the correct flow. The ok branch emits a
        // resume fact when employments exist, so a subsequent !ok/error for the
        // SAME user must NOT downgrade to a second `new` emission: the module
        // guard keys on the deterministic semantic id (user + flow), so an ok
        // resolution and a later error resolve to the same id and collapse.
        const resolvedFlow = classifyDriverOnboardingFlow({
          ok: userDetailsResponse.ok,
          isOwner: Boolean(isOwner) || Boolean(hasOwnedCompanies),
          employments: userDetailsResponse.data?.employments ?? [],
        });
        setFlow(resolvedFlow);
        if (!onboardingStartedRef.current) {
          onboardingStartedRef.current = true;
          emitOnboardingStartedOnce(resolvedFlow, userDetailsResponse.data?.id);
        }
      } catch (error) {
        setCompanyResolved(false);
        setHasVehicles(false);
        // On error, classify consistently with the ok=false branch: a new
        // onboarding (no resolved resume fact exists). Emit exactly once with
        // the SAME deterministic eventId as the ok=false path for this session
        // so a remount/rejected-promise collapse to a single emission.
        if (!onboardingStartedRef.current) {
          onboardingStartedRef.current = true;
          // No authenticated user id on error -> session-scoped stable key,
          // which is reused across remounts of the same session.
          emitOnboardingStartedOnce('new');
        }
      } finally {
        setIsChecking(false);
      }
    };

    checkUserData();
  }, []);

  const handleContinue = () => {
    router.push(resolveOnboardingContinueTarget(companyResolved, hasVehicles));
  };

  if (isChecking) {
    return <LoadingScreen />;
  }

  // UI copy: permissions-only when company+vehicle already known; otherwise welcome.
  const showPermissionsCopy = companyResolved && hasVehicles;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors.background }}>
      <StatusBar style="dark" />
      <Surface style={styles.container}>
        <View style={styles.imageContainer}>
          <MaterialCommunityIcons
            name={showPermissionsCopy ? 'shield-check-outline' : 'account-check-outline'}
            size={ICON_SIZE}
            color={theme.colors.primary}
          />
        </View>

        <View style={styles.contentContainer}>
          <Text style={[styles.title, { color: theme.colors.primary }]}>
            {showPermissionsCopy
              ? t('onboardingWelcomeTitlePermissions', 'Configure as Permissões')
              : t('onboardingWelcomeTitle', 'Bem-vindo ao nosso aplicativo!')}
          </Text>

          <Text style={styles.description}>
            {showPermissionsCopy
              ? t(
                  'onboardingWelcomeDescriptionPermissions',
                  'Para usar o aplicativo neste dispositivo, precisamos configurar as permissões necessárias.',
                )
              : t(
                  'onboardingWelcomeDescription',
                  'Vamos configurar algumas informações importantes antes de começar. Isso nos ajudará a personalizar sua experiência.',
                )}
          </Text>

          <View style={styles.stepsContainer}>
            <View style={[styles.stepIndicator, { backgroundColor: theme.colors.primary }]} />
            {!showPermissionsCopy && (
              <>
                <View
                  style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]}
                />
                <View
                  style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]}
                />
                <View
                  style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]}
                />
              </>
            )}
          </View>

          <Button
            mode="contained"
            style={styles.button}
            contentStyle={styles.buttonContent}
            onPress={handleContinue}
          >
            {t('continue', 'Continuar')}
          </Button>
        </View>
      </Surface>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  contentContainer: {
    flex: 1,
    width: '100%',
    paddingHorizontal: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  description: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 24,
  },
  stepsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 24,
  },
  stepIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginHorizontal: 4,
  },
  button: {
    width: '100%',
    marginTop: 8,
  },
  buttonContent: {
    height: 48,
  },
});
